import { useState, type ReactNode } from 'react';
import { AtSign, Bell, ChevronDown, ExternalLink, Mail, Plus, Search, Settings, Trash2 } from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  Input,
  Tooltip,
  type BadgeVariant,
  type ButtonVariant,
} from '@/components/ui';
import { StateMark } from '@/components/brand/StateMark';
import { Code, Section, SubTitle } from './Section';

const buttonVariants: ButtonVariant[] = ['primary', 'secondary', 'ghost', 'destructive', 'link'];
const badgeVariants: BadgeVariant[] = ['neutral', 'coral', 'urgent', 'warn', 'success', 'info', 'ai'];

function Row({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-5">
      <SubTitle>{title}</SubTitle>
      {children}
    </div>
  );
}

export function PrimitivesSection() {
  const [loading, setLoading] = useState(false);

  return (
    <Section
      id="primitives"
      title="Примитивы"
      lead="Настоящие компоненты из @/components/ui во всех вариантах и размерах. Внешний вид — только через variant/size (R4). Полный состав библиотеки появляется в фазе 3."
    >
      <Row title="Button">
        <div className="flex flex-col gap-3">
          {(['sm', 'md', 'lg'] as const).map((size) => (
            <div key={size} className="flex flex-wrap items-center gap-2">
              <Code>{size}</Code>
              {buttonVariants.map((v) => (
                <Button key={v} variant={v} size={size}>
                  {v}
                </Button>
              ))}
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <Code>состояния</Code>
            <Button leftIcon={<Plus />}>С иконкой</Button>
            <Button variant="secondary" rightIcon={<ChevronDown />}>
              Меню
            </Button>
            <Button
              loading={loading}
              onClick={() => {
                setLoading(true);
                setTimeout(() => setLoading(false), 1500);
              }}
            >
              Загрузка
            </Button>
            <Button disabled>Недоступна</Button>
          </div>
        </div>
      </Row>

      <Row title="IconButton · Tooltip">
        <div className="flex flex-wrap items-center gap-2">
          {(['sm', 'md', 'lg'] as const).map((size) => (
            <Tooltip key={size} content={`size=${size}`}>
              <IconButton aria-label="Уведомления" size={size} variant="secondary">
                <Bell />
              </IconButton>
            </Tooltip>
          ))}
          <IconButton aria-label="Настройки" variant="ghost">
            <Settings />
          </IconButton>
          <IconButton aria-label="Добавить" variant="primary">
            <Plus />
          </IconButton>
          <IconButton aria-label="Удалить" variant="destructive">
            <Trash2 />
          </IconButton>
        </div>
      </Row>

      <Row title="Badge">
        <div className="flex flex-wrap items-center gap-2">
          {badgeVariants.map((v) => (
            <Badge key={v} variant={v}>
              {v}
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {badgeVariants.map((v) => (
            <Badge key={v} variant={v} dot>
              {v}
            </Badge>
          ))}
        </div>
      </Row>

      <Row title="Input">
        <div className="grid gap-4 md:grid-cols-3">
          <Input label="Поиск" placeholder="Найти узел…" leadingIcon={<Search />} />
          <Input
            label="Почта"
            placeholder="name@example.test"
            helper="Рабочий адрес"
            trailingIcon={<Mail />}
          />
          <Input label="Токен" defaultValue="xoxb-…" error="Токен отозван" />
        </div>
      </Row>

      <Row title="Avatar">
        <div className="flex flex-wrap items-end gap-3">
          {(['xs', 'sm', 'md', 'lg', 'xl'] as const).map((s) => (
            <Avatar key={s} size={s} name="Анна Смирнова" />
          ))}
          <Avatar size="lg" name="Kaname Hub" src="/nonexistent.png" />
        </div>
      </Row>

      <Row title="DropdownMenu">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" rightIcon={<ChevronDown />}>
              Действия
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Узел host-338</DropdownMenuLabel>
            <DropdownMenuItem leftIcon={<ExternalLink />} shortcut="⌘O">
              Открыть
            </DropdownMenuItem>
            <DropdownMenuItem leftIcon={<Settings />}>Настроить</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive leftIcon={<Trash2 />}>
              Удалить
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Row>

      <Row title="Card · EmptyState">
        <div className="grid gap-4 md:grid-cols-2">
          <Card interactive>
            <CardHeader>
              <CardTitle>Интерактивная карточка</CardTitle>
              <CardDescription>Наведи — рецепт lift. Радиус lg (16), не круглее холста.</CardDescription>
            </CardHeader>
            <CardBody>
              <p className="text-sm text-fg-muted">Содержимое карточки на семантических токенах.</p>
            </CardBody>
          </Card>
          <EmptyState
            icon={<AtSign />}
            title="Пока пусто"
            description="Подключите источник, и здесь появятся упоминания."
            action={{ label: 'Подключить' }}
          />
        </div>
      </Row>

      <Row title="EmptyState — вкладка целиком">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex min-h-96 flex-col rounded-lg border border-line bg-canvas">
            <EmptyState
              frame="page"
              className="flex-1"
              mark={<StateMark kind="failed" done={3} total={4} />}
              title="Не удалось открыть «Инфраструктуру»"
              description="Шаг «Меню»: сервер не ответил за 30 с."
              action={{ label: 'Повторить', variant: 'primary' }}
              secondaryAction={{ label: 'Назад' }}
              details={'K-7F3A · 22.10.2026 03:14:07\nGET /api/apps/infra/menu — 504 за 30 с'}
            />
          </div>
          <EmptyState
            icon={<Search />}
            title="Ничего не нашлось"
            description="Попробуйте другое слово — поиск понимает транслит и набор не в той раскладке."
          />
        </div>
        <p className="text-xs text-fg-muted">
          <Code>frame="page"</Code> — вкладка целиком (не найдено, нет доступа, не открылось, сбой): без
          карточки, по центру, заголовок h1 для фокуса; знак передаёт вызывающий в <Code>mark</Code> — ui не
          знает о бренде. <Code>frame="card"</Code> — пустота внутри страницы, значок в круге. Действия —{' '}
          <Code>action</Code> (secondary) и <Code>secondaryAction</Code> (ghost) с <Code>variant</Code>;{' '}
          <Code>status</Code> — строка шага; <Code>details</Code> — «Подробности для поддержки» со
          «Скопировать».
        </p>
      </Row>
    </Section>
  );
}
